import React, {
  Children,
  cloneElement,
  isValidElement,
  useEffect,
  useState,
} from "react";
import LazyLoad, { LazyLoadProps } from "react-lazyload";
import styled from "styled-components";

interface IPlaceholder {
  height: number | string;
}

const Placeholder = styled.div<IPlaceholder>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  // Add appropriate height or minHeight to your placeholder element
  // for better lazyload performance
  height: ${({ height }: IPlaceholder): string =>
    typeof height === "number" ? height + "px" : height};

  // loader animation
  @keyframes loadAnimation {
    0%,
    100% {
      box-shadow: 0 -3em 0 0.2em, 2em -2em 0 0em, 3em 0 0 -1em, 2em 2em 0 -1em,
        0 3em 0 -1em, -2em 2em 0 -1em, -3em 0 0 -1em, -2em -2em 0 0;
    }
    12.5% {
      box-shadow: 0 -3em 0 0, 2em -2em 0 0.2em, 3em 0 0 0, 2em 2em 0 -1em,
        0 3em 0 -1em, -2em 2em 0 -1em, -3em 0 0 -1em, -2em -2em 0 -1em;
    }
    25% {
      box-shadow: 0 -3em 0 -0.5em, 2em -2em 0 0, 3em 0 0 0.2em, 2em 2em 0 0,
        0 3em 0 -1em, -2em 2em 0 -1em, -3em 0 0 -1em, -2em -2em 0 -1em;
    }
    37.5% {
      box-shadow: 0 -3em 0 -1em, 2em -2em 0 -1em, 3em 0em 0 0, 2em 2em 0 0.2em,
        0 3em 0 0em, -2em 2em 0 -1em, -3em 0em 0 -1em, -2em -2em 0 -1em;
    }
    50% {
      box-shadow: 0 -3em 0 -1em, 2em -2em 0 -1em, 3em 0 0 -1em, 2em 2em 0 0em,
        0 3em 0 0.2em, -2em 2em 0 0, -3em 0em 0 -1em, -2em -2em 0 -1em;
    }
    62.5% {
      box-shadow: 0 -3em 0 -1em, 2em -2em 0 -1em, 3em 0 0 -1em, 2em 2em 0 -1em,
        0 3em 0 0, -2em 2em 0 0.2em, -3em 0 0 0, -2em -2em 0 -1em;
    }
    75% {
      box-shadow: 0em -3em 0 -1em, 2em -2em 0 -1em, 3em 0em 0 -1em,
        2em 2em 0 -1em, 0 3em 0 -1em, -2em 2em 0 0, -3em 0em 0 0.2em,
        -2em -2em 0 0;
    }
    87.5% {
      box-shadow: 0em -3em 0 0, 2em -2em 0 -1em, 3em 0 0 -1em, 2em 2em 0 -1em,
        0 3em 0 -1em, -2em 2em 0 0, -3em 0em 0 0, -2em -2em 0 0.2em;
    }
  }

  // loader
  &::after {
    content: "";
    color: #999;
    font-size: 10px; // loader size
    margin: 100px auto;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    position: relative;
    text-indent: -9999em;
    animation: loadAnimation 1.3s infinite linear;
    transform: translateZ(0);
  }
`;

/**
 * LazyloadImage
 *
 * Lazyload the children image with Browser-level lazy loading
 * or with react-lazyload if the current browser don't yet support lazy-loading natively
 *
 * @param height - height of the children image (required for the placeholder)
 * @param children - only 1 image should be passed as children
 */
export const LazyloadImage = (props: LazyLoadProps): JSX.Element => {
  const [isLoadingSupported, setLoadingSupported] = useState(false);

  useEffect(() => {
    if ("loading" in HTMLImageElement.prototype) {
      // supported in browser
      setLoadingSupported(true);
    }
  }, []);

  const childrenWithProps = Children.map(props.children, (child, i) => {
    if (isValidElement(child)) {
      const { className } = child.props;

      return cloneElement(child, {
        loading: "lazy",
        className: `lazyload-browser-level${className ? ` ${className}` : ""}`,
        height: props.height ?? 200,
      });
    }
  });

  /**
   * Image with loading attribute (Browser-level lazy loading)
   *
   * Supported by most popular Chromium-powered browsers
   * (Chrome, Edge, Opera) and Firefox.
   * The implementation for WebKit (Safari) is in progress.
   */
  if (isLoadingSupported) {
    return <>{childrenWithProps}</>;
  }

  /**
   * Handle browsers that don't yet support lazy-loading natively
   * react-lazyload (third-party library)
   */
  return (
    <LazyLoad
      offset={props.offset ?? 0}
      once={props.once ?? true}
      style={{ height: "100%" }}
      placeholder={
        props.placeholder ?? (
          <Placeholder
            className="lazyload-placeholder"
            height={props.height ?? 200}
          />
        )
      }
      {...props}
    >
      {props.children}
    </LazyLoad>
  );
};
